import type { SortDescriptor } from "react-stately";

import { memo, useCallback, useEffect, useState } from "react";

import type { TeamLeadFilterType } from "../../TeamLeadDropdownFilter";

import {
  assignedToMeTableConfig,
  teamleadCompletedTableConfig,
  teamleadDueTodayTableConfig,
  teamleadOverdueTableConfig,
} from "../../../config/tables/teamlead";
import { useResetFilterOnEmptySearch } from "../../../hooks/shared";
import {
  useTeamLeadRequestCounts,
  useTeamLeadRequestData,
  useTeamLeadSectionStates,
} from "../../../hooks/teamlead";
import { LazyConfigurableTable } from "../../tables/LazyConfigurableTable";

interface AllRequestsTabProps {
  onFilterChange?: (filter: TeamLeadFilterType) => void;
  searchValue?: string;
  selectedFilter?: TeamLeadFilterType;
}

type SectionType = "awaiting-reply" | "in-queue" | "others" | "processing";

const SECTIONS: { id: SectionType; title: string }[] = [
  { id: "processing", title: "Processing" },
  { id: "awaiting-reply", title: "Awaiting Reply" },
  { id: "in-queue", title: "In Queue" },
  { id: "others", title: "Others" },
];

// Default sort configurations for single-table views
const SINGLE_TABLE_SORT_DEFAULTS: Record<
  "completed" | "due-today" | "overdue",
  { column: string; direction: "ascending" | "descending" }
> = {
  completed: {
    column: "completedDate",
    direction: "descending",
  },
  "due-today": {
    column: "dueDate",
    direction: "ascending",
  },
  overdue: {
    column: "dueDate",
    direction: "ascending",
  },
};

function AllRequestsTabComponent({
  onFilterChange,
  searchValue = "",
  selectedFilter = "open",
}: AllRequestsTabProps) {
  const { data: countsData, isLoading: isCountsLoading } =
    useTeamLeadRequestCounts({ search: searchValue });

  // Determine if we should use single-table mode
  const isSingleTableMode =
    selectedFilter === "completed" ||
    selectedFilter === "due-today" ||
    selectedFilter === "overdue";

  // Manage independent state for each section (each has its own default sort)
  const { createSectionHandlers, sectionStates } = useTeamLeadSectionStates();

  // Manage state for single-table view
  const [singleTableState, setSingleTableState] = useState<{
    currentPage: number;
    itemsPerPage: number;
    sortDescriptor: SortDescriptor;
  }>({
    currentPage: 1,
    itemsPerPage: 10,
    sortDescriptor:
      SINGLE_TABLE_SORT_DEFAULTS[
        selectedFilter as "completed" | "due-today" | "overdue"
      ] || SINGLE_TABLE_SORT_DEFAULTS.completed,
  });

  const [cachedTotalPages, setCachedTotalPages] = useState<null | number>(null);
  const [cachedTotalItems, setCachedTotalItems] = useState<null | number>(null);

  // Single table handlers
  const handleSingleTablePageChange = useCallback((page: number) => {
    setSingleTableState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  const handleSingleTableItemsPerPageChange = useCallback(
    (itemsPerPage: number) => {
      setSingleTableState((prev) => ({
        ...prev,
        currentPage: 1,
        itemsPerPage,
      }));
    },
    [],
  );

  const handleSingleTableSortChange = useCallback(
    (sortDescriptor: SortDescriptor) => {
      setSingleTableState((prev) => ({
        ...prev,
        sortDescriptor,
      }));
    },
    [],
  );

  // Fetch single-table data if in single-table mode
  const singleTableData = useTeamLeadRequestData({
    direction:
      singleTableState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: isSingleTableMode,
    filter: selectedFilter,
    limit: singleTableState.itemsPerPage,
    page: singleTableState.currentPage,
    search: searchValue,
    skipCount: false,
    sortBy: singleTableState.sortDescriptor.column as string,
    tab: "all-requests",
    viewMode: "single-table",
  });

  useEffect(() => {
    if (singleTableState.currentPage === 1) {
      if (singleTableData.data?.pagination?.totalPages) {
        setCachedTotalPages(singleTableData.data.pagination.totalPages);
      }
      if (singleTableData.data?.total != null) {
        setCachedTotalItems(singleTableData.data.total);
      }
    }
  }, [
    singleTableState.currentPage,
    singleTableData.data?.pagination?.totalPages,
    singleTableData.data?.total,
  ]);

  useEffect(() => {
    setSingleTableState((prev) => ({ ...prev, currentPage: 1 }));
    setCachedTotalPages(null);
    setCachedTotalItems(null);
  }, [selectedFilter, searchValue]);

  // Fetch data for all sections at the top level (React Hooks rules)
  // Only fetch section data if NOT in single-table mode
  const processingState = sectionStates.processing;
  const awaitingReplyState = sectionStates["awaiting-reply"];
  const inQueueState = sectionStates["in-queue"];
  const othersState = sectionStates.others;

  const processingData = useTeamLeadRequestData({
    direction:
      processingState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: processingState.itemsPerPage,
    page: processingState.currentPage,
    search: searchValue,
    section: "processing",
    skipCount: false, // Always get count from data query
    sortBy: processingState.sortDescriptor.column as string,
    tab: "all-requests",
    viewMode: "section",
  });

  const awaitingReplyData = useTeamLeadRequestData({
    direction:
      awaitingReplyState.sortDescriptor.direction === "ascending"
        ? "asc"
        : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: awaitingReplyState.itemsPerPage,
    page: awaitingReplyState.currentPage,
    search: searchValue,
    section: "awaiting-reply",
    skipCount: false, // Always get count from data query
    sortBy: awaitingReplyState.sortDescriptor.column as string,
    tab: "all-requests",
    viewMode: "section",
  });

  const inQueueData = useTeamLeadRequestData({
    direction:
      inQueueState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: inQueueState.itemsPerPage,
    page: inQueueState.currentPage,
    search: searchValue,
    section: "in-queue",
    skipCount: false, // Always get count from data query
    sortBy: inQueueState.sortDescriptor.column as string,
    tab: "all-requests",
    viewMode: "section",
  });

  const othersData = useTeamLeadRequestData({
    direction:
      othersState.sortDescriptor.direction === "ascending" ? "asc" : "desc",
    enabled: !isSingleTableMode,
    filter: selectedFilter,
    limit: othersState.itemsPerPage,
    page: othersState.currentPage,
    search: searchValue,
    section: "others",
    skipCount: false, // Always get count from data query
    sortBy: othersState.sortDescriptor.column as string,
    tab: "all-requests",
    viewMode: "section",
  });

  const sectionQueries = [
    processingData,
    awaitingReplyData,
    inQueueData,
    othersData,
  ];

  useResetFilterOnEmptySearch({
    isAlreadyBroadest: selectedFilter === "all",
    isReady: isSingleTableMode
      ? singleTableData.isSuccess && !singleTableData.isFetching
      : sectionQueries.every((q) => q.isSuccess && !q.isFetching),
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: isSingleTableMode
      ? (singleTableData.data?.total ?? 0)
      : sectionQueries.reduce((sum, q) => sum + (q.data?.total ?? 0), 0),
  });

  // Get appropriate table config based on filter
  const getSingleTableConfig = () => {
    switch (selectedFilter) {
      case "completed": {
        return teamleadCompletedTableConfig;
      }
      case "due-today": {
        return teamleadDueTodayTableConfig;
      }
      case "overdue": {
        return teamleadOverdueTableConfig;
      }
      default: {
        return assignedToMeTableConfig;
      }
    }
  };

  // Render single-table view
  if (isSingleTableMode) {
    const { data, isLoading } = singleTableData;
    const tableConfig = getSingleTableConfig();

    // Get count based on selected filter
    const getCount = (): number | undefined => {
      if (!countsData?.counts.allRequests) {
        return undefined;
      }

      return undefined;
    };

    const useDataTotal = searchValue || isSingleTableMode;
    const effectiveTotal = useDataTotal
      ? (data?.total ?? cachedTotalItems)
      : (getCount() ?? null);
    const effectiveCount = useDataTotal ? data?.total : getCount();

    const computedTotalPages = effectiveTotal
      ? Math.ceil(effectiveTotal / singleTableState.itemsPerPage)
      : null;
    const effectiveTotalPages = computedTotalPages ?? cachedTotalPages;

    return (
      <div className="space-y-6">
        <LazyConfigurableTable
          config={tableConfig}
          count={effectiveCount}
          data={data?.requests || []}
          isCountLoading={isCountsLoading}
          isLoading={isLoading}
          paginationProps={{
            metadata: {
              currentPage: singleTableState.currentPage,
              itemsPerPage: singleTableState.itemsPerPage,
              totalItems: effectiveTotal,
              totalPages: effectiveTotalPages,
            },
            onItemsPerPageChange: handleSingleTableItemsPerPageChange,
            onPageChange: handleSingleTablePageChange,
            pageSizeOptions: [10, 25, 50, 100],
            showPageSizeSelector: true,
          }}
          setSortDescriptor={handleSingleTableSortChange}
          sortDescriptor={singleTableState.sortDescriptor}
          useAdvancedSorting={true}
        />
      </div>
    );
  }

  // Render multi-section view (for "all" and "open" filters)
  // Map sections to their data
  const sectionDataMap = {
    "awaiting-reply": awaitingReplyData,
    "in-queue": inQueueData,
    others: othersData,
    processing: processingData,
  };

  const sectionCountsMap: Record<SectionType, number | undefined> = {
    "awaiting-reply": countsData?.counts.allRequests.awaitingReply,
    "in-queue": countsData?.counts.allRequests.inQueue,
    others: countsData?.counts.allRequests.others,
    processing: countsData?.counts.allRequests.processing,
  };

  return (
    <div className="space-y-6">
      {SECTIONS.map((section) => {
        const sectionState = sectionStates[section.id];
        const handlers = createSectionHandlers(section.id);
        const { data, isLoading } = sectionDataMap[section.id];

        const effectiveTotal = searchValue
          ? (data?.total ?? null)
          : (sectionCountsMap[section.id] ?? null);
        const effectiveCount = searchValue
          ? data?.total
          : sectionCountsMap[section.id];

        return (
          <div key={section.id}>
            <LazyConfigurableTable
              config={{
                ...assignedToMeTableConfig,
                id: `all-requests-${section.id}`,
                title: section.title,
              }}
              count={effectiveCount}
              data={data?.requests || []}
              isCountLoading={isCountsLoading}
              isLoading={isLoading}
              paginationProps={{
                metadata: {
                  currentPage: sectionState.currentPage,
                  itemsPerPage: sectionState.itemsPerPage,
                  totalItems: effectiveTotal,
                  totalPages: effectiveTotal
                    ? Math.ceil(effectiveTotal / sectionState.itemsPerPage)
                    : null,
                },
                onItemsPerPageChange: handlers.onItemsPerPageChange,
                onPageChange: handlers.onPageChange,
                pageSizeOptions: [10, 25, 50, 100],
                showPageSizeSelector: true,
              }}
              setSortDescriptor={handlers.onSortChange}
              sortDescriptor={sectionState.sortDescriptor}
              useAdvancedSorting={true}
            />
          </div>
        );
      })}
    </div>
  );
}

export const AllRequestsTab = memo(AllRequestsTabComponent);
